import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Search, AlertTriangle, Clock, TrendingDown, Flag, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Supply {
  supply_id: string;
  item_name: string;
  category: string;
  manufacturer: string;
  catalog_number: string;
  lot_number: string;
  expiration_date: string | null;
  quantity_on_hand: number;
  par_level_min: number | null;
  par_level_max: number | null;
  unit_cost: number;
  storage_location: string;
  days_until_expiry: number | null;
  stock_status: string;
  needs_reorder: boolean;
  active_alerts_count: number;
  total_value: number;
}

interface AlertStats {
  expired: number;
  expiring_soon: number;
  below_par: number;
  flagged_reorder: number;
}

export default function SuppliesListRoute() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [stockStatusFilter, setStockStatusFilter] = useState<string>('All');
  const [expirationFilter, setExpirationFilter] = useState<string>('All');

  // Fetch supplies
  const { data: supplies = [], isLoading } = useQuery({
    queryKey: ['supplies', categoryFilter, stockStatusFilter, expirationFilter, searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('or_surgical_supplies')
        .select('*')
        .eq('is_active', true)
        .order('item_name');

      // Category filter
      if (categoryFilter !== 'All') {
        query = query.eq('category', categoryFilter);
      }

      // Stock status filter
      if (stockStatusFilter !== 'All') {
        query = query.eq('stock_status', stockStatusFilter);
      }

      // Expiration filter
      if (expirationFilter === 'Expired') {
        query = query.lt('days_until_expiry', 0);
      } else if (expirationFilter === '30d') {
        query = query.gte('days_until_expiry', 0).lte('days_until_expiry', 30);
      } else if (expirationFilter === '60d') {
        query = query.gte('days_until_expiry', 0).lte('days_until_expiry', 60);
      } else if (expirationFilter === '90d') {
        query = query.gte('days_until_expiry', 0).lte('days_until_expiry', 90);
      }

      // Search
      if (searchTerm) {
        query = query.or(`item_name.ilike.%${searchTerm}%,manufacturer.ilike.%${searchTerm}%,catalog_number.ilike.%${searchTerm}%,lot_number.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Supply[];
    },
  });

  // Calculate alert stats
  const alertStats: AlertStats = supplies.reduce(
    (acc, supply) => {
      if (supply.days_until_expiry !== null && supply.days_until_expiry < 0) acc.expired++;
      if (supply.days_until_expiry !== null && supply.days_until_expiry >= 0 && supply.days_until_expiry <= 30) acc.expiring_soon++;
      if (supply.stock_status === 'Low Stock' || supply.stock_status === 'Out of Stock') acc.below_par++;
      if (supply.needs_reorder) acc.flagged_reorder++;
      return acc;
    },
    { expired: 0, expiring_soon: 0, below_par: 0, flagged_reorder: 0 }
  );

  // Stock status badge
  const getStockBadge = (status: string) => {
    const styles = {
      'In Stock': 'bg-pt-green/20 text-pt-green border-pt-green/30',
      'Low Stock': 'bg-pt-amber/20 text-pt-amber border-pt-amber/30',
      'Out of Stock': 'bg-pt-red/20 text-pt-red border-pt-red/30',
      'Overstocked': 'bg-pt-cyan/20 text-pt-cyan border-pt-cyan/30',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles[status as keyof typeof styles] || 'bg-pt-text-dim/20 text-pt-text-muted border-pt-input-border'}`}>
        {status}
      </span>
    );
  };

  // Expiration badge
  const getExpirationBadge = (days: number | null) => {
    if (days === null) return <span className="text-xs text-pt-text0">No expiration</span>;
    if (days < 0) return <span className="text-xs text-pt-red font-medium">Expired {Math.abs(days)}d ago</span>;
    if (days <= 30) return <span className="text-xs text-pt-amber font-medium">{days}d remaining</span>;
    if (days <= 90) return <span className="text-xs text-pt-cyan">{days}d remaining</span>;
    return <span className="text-xs text-pt-text-muted">{days}d remaining</span>;
  };

  return (
    <div className="min-h-full bg-pt-bg">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-pt-gold font-['Rajdhani'] tracking-wide mb-2">
              Surgical Supplies Inventory
            </h1>
            <p className="text-pt-text-muted text-sm">
              Manage sutures, dressings, implants, disposables, and instruments
            </p>
          </div>
          <Link
            to="/supplies/new"
            className="flex items-center gap-2 px-4 py-2 bg-pt-gold text-pt-bg rounded hover:bg-pt-gold-light transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Supply
          </Link>
        </div>

        {/* Alert Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Expired */}
          <div className="bg-pt-surface border border-pt-red/30 rounded p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-5 h-5 text-pt-red" />
              <span className="text-2xl font-bold text-pt-red">{alertStats.expired}</span>
            </div>
            <div className="text-sm text-pt-text-muted">Expired Items</div>
            <div className="text-xs text-pt-red mt-1">Critical - Remove from stock</div>
          </div>

          {/* Expiring Soon */}
          <div className="bg-pt-surface border border-pt-amber/30 rounded p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-pt-amber" />
              <span className="text-2xl font-bold text-pt-amber">{alertStats.expiring_soon}</span>
            </div>
            <div className="text-sm text-pt-text-muted">Expiring &lt;30 Days</div>
            <div className="text-xs text-pt-amber mt-1">Review for priority use</div>
          </div>

          {/* Below Par */}
          <div className="bg-pt-surface border border-pt-amber/30 rounded p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="w-5 h-5 text-pt-amber" />
              <span className="text-2xl font-bold text-pt-amber">{alertStats.below_par}</span>
            </div>
            <div className="text-sm text-pt-text-muted">Below Par Level</div>
            <div className="text-xs text-pt-amber mt-1">Stock running low</div>
          </div>

          {/* Flagged for Reorder */}
          <div className="bg-pt-surface border border-pt-cyan/30 rounded p-4">
            <div className="flex items-center justify-between mb-2">
              <Flag className="w-5 h-5 text-pt-cyan" />
              <span className="text-2xl font-bold text-pt-cyan">{alertStats.flagged_reorder}</span>
            </div>
            <div className="text-sm text-pt-text-muted">Flagged for Reorder</div>
            <div className="text-xs text-pt-cyan mt-1">Action required</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-pt-surface border border-pt-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pt-text0" />
              <input
                type="text"
                placeholder="Search supplies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded pl-10 pr-3 py-2 text-sm text-pt-text placeholder-pt-text-muted focus:outline-none focus:border-pt-gold"
              />
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-sm text-pt-text focus:outline-none focus:border-pt-gold"
              >
                <option value="All">All Categories</option>
                <option value="Sutures">Sutures</option>
                <option value="Dressings">Dressings</option>
                <option value="Implants">Implants</option>
                <option value="Disposables">Disposables</option>
                <option value="Instruments">Instruments</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Stock Status Filter */}
            <div>
              <select
                value={stockStatusFilter}
                onChange={(e) => setStockStatusFilter(e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-sm text-pt-text focus:outline-none focus:border-pt-gold"
              >
                <option value="All">All Stock Status</option>
                <option value="In Stock">In Stock</option>
                <option value="Low Stock">Low Stock</option>
                <option value="Out of Stock">Out of Stock</option>
                <option value="Overstocked">Overstocked</option>
              </select>
            </div>

            {/* Expiration Filter */}
            <div>
              <select
                value={expirationFilter}
                onChange={(e) => setExpirationFilter(e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-sm text-pt-text focus:outline-none focus:border-pt-gold"
              >
                <option value="All">All Expiration</option>
                <option value="Expired">Expired</option>
                <option value="30d">Expiring &lt;30 Days</option>
                <option value="60d">Expiring &lt;60 Days</option>
                <option value="90d">Expiring &lt;90 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-pt-surface border border-pt-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-pt-border">
                  <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Item</th>
                  <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Category</th>
                  <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Lot #</th>
                  <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Expiration</th>
                  <th className="text-right p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Qty</th>
                  <th className="text-right p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Par Min/Max</th>
                  <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Location</th>
                  <th className="text-left p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Status</th>
                  <th className="text-right p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Value</th>
                  <th className="text-center p-3 text-xs font-semibold text-pt-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-pt-text-muted">
                      Loading supplies...
                    </td>
                  </tr>
                ) : supplies.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-pt-text-muted">
                      No supplies found matching your filters
                    </td>
                  </tr>
                ) : (
                  supplies.map((supply) => (
                    <tr key={supply.supply_id} className="border-b border-pt-border hover:bg-pt-bg transition-colors">
                      <td className="p-3">
                        <div className="text-sm text-pt-text font-medium">{supply.item_name}</div>
                        <div className="text-xs text-pt-text-muted mt-1">
                          {supply.manufacturer} {supply.catalog_number && `• ${supply.catalog_number}`}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-pt-text-muted">{supply.category}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-xs text-pt-text-muted font-mono">{supply.lot_number || '—'}</span>
                      </td>
                      <td className="p-3">
                        {getExpirationBadge(supply.days_until_expiry)}
                      </td>
                      <td className="p-3 text-right">
                        <span className={`text-sm font-medium ${supply.quantity_on_hand === 0 ? 'text-pt-red' : supply.stock_status === 'Low Stock' ? 'text-pt-amber' : 'text-pt-text'}`}>
                          {supply.quantity_on_hand}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-xs text-pt-text-muted">
                          {supply.par_level_min || '—'} / {supply.par_level_max || '—'}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="text-xs text-pt-text-muted">{supply.storage_location}</span>
                      </td>
                      <td className="p-3">
                        {getStockBadge(supply.stock_status)}
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-sm text-pt-text-muted">${supply.total_value.toFixed(2)}</span>
                      </td>
                      <td className="p-3 text-center">
                        <Link
                          to={`/supplies/${supply.supply_id}`}
                          className="text-pt-gold hover:text-pt-gold-light text-sm font-medium"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          {!isLoading && supplies.length > 0 && (
            <div className="border-t border-pt-border p-4 bg-pt-bg">
              <div className="flex items-center justify-between text-sm">
                <div className="text-pt-text-muted">
                  <span className="font-medium text-pt-text">{supplies.length}</span> items displayed
                </div>
                <div className="text-pt-text-muted">
                  Total value: <span className="font-medium text-pt-gold">
                    ${supplies.reduce((sum, s) => sum + s.total_value, 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
